import { migratePartId } from "./partIdMigration";
import { PART_BY_ID } from "./parts";
import { SLOT_BY_ID } from "./slots";
import type { PartDefinition } from "./types";

export type ConnectorSide = "top" | "bottom" | "left" | "right" | "face";

export interface PlacedPart {
  instanceId: string;
  partId: string;
  /** Center position in grid units. */
  x: number;
  y: number;
  rotation?: number;
  scaleX?: number;
}

export function createInstanceId(): string {
  return `p-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

export function isPlacedPartArray(value: unknown): value is PlacedPart[] {
  return (
    Array.isArray(value) &&
    value.every(
      (p) =>
        typeof p === "object" &&
        p !== null &&
        typeof (p as PlacedPart).instanceId === "string" &&
        typeof (p as PlacedPart).partId === "string" &&
        typeof (p as PlacedPart).x === "number" &&
        typeof (p as PlacedPart).y === "number",
    )
  );
}

export function migrateSlotPlacements(slots: Record<string, string>): PlacedPart[] {
  const placements: PlacedPart[] = [];
  let index = 0;

  for (const [slotId, rawPartId] of Object.entries(slots)) {
    const slot = SLOT_BY_ID[slotId];
    const partId = migratePartId(rawPartId);
    if (!slot || !PART_BY_ID[partId]) continue;

    let rotation = 0;
    let scaleX = 1;
    if (slotId === "slot-fin-left") {
      rotation = 0.11;
      scaleX = -1;
    } else if (slotId === "slot-fin-right") {
      rotation = -0.11;
      scaleX = 1;
    }

    placements.push({
      instanceId: `migrated-${index++}`,
      partId,
      x: slot.offsetX,
      y: slot.offsetY,
      rotation,
      scaleX,
    });
  }

  return placements;
}

function migratePlacement(placed: PlacedPart): PlacedPart {
  return { ...placed, partId: migratePartId(placed.partId) };
}

export function normalizePlacements(raw: unknown): PlacedPart[] {
  if (isPlacedPartArray(raw)) {
    return raw
      .map(migratePlacement)
      .filter((p) => Boolean(PART_BY_ID[p.partId]));
  }
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    return migrateSlotPlacements(raw as Record<string, string>);
  }
  return [];
}

/** Starter rocket shown until the player saves a custom build. */
export const DEFAULT_ROCKET_PLACEMENTS: PlacedPart[] = [
  { instanceId: "default-nose", partId: "nose_cone-blue", x: 0, y: -5 },
  { instanceId: "default-body", partId: "body_window-blue", x: 0, y: -2.5 },
  { instanceId: "default-engine", partId: "engine_twin-blue", x: 0, y: 0 },
];

export function hasCustomRocket(placements: PlacedPart[]): boolean {
  return placements.length > 0;
}

export function rocketPlacementsForDisplay(saved: PlacedPart[]): PlacedPart[] {
  return hasCustomRocket(saved) ? saved : DEFAULT_ROCKET_PLACEMENTS;
}

export function partCategoriesInBuild(placements: PlacedPart[]): Set<string> {
  const cats = new Set<string>();
  for (const p of placements) {
    const part = PART_BY_ID[p.partId];
    if (part) cats.add(part.category);
  }
  return cats;
}

export function isRocketFlyable(placements: PlacedPart[]): boolean {
  const cats = partCategoriesInBuild(placements);
  return cats.has("nose") && cats.has("fuselage") && cats.has("engine");
}

export function placementBounds(placements: PlacedPart[]): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} | null {
  if (!placements.length) return null;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const placed of placements) {
    const part = PART_BY_ID[placed.partId];
    if (!part) continue;
    const hw = part.dimensions.gridW / 2;
    const hh = part.dimensions.gridH / 2;
    minX = Math.min(minX, placed.x - hw);
    minY = Math.min(minY, placed.y - hh);
    maxX = Math.max(maxX, placed.x + hw);
    maxY = Math.max(maxY, placed.y + hh);
  }

  if (!Number.isFinite(minX)) return null;
  return { minX, minY, maxX, maxY };
}

export function categoryZIndex(part: PartDefinition): number {
  switch (part.category) {
    case "window":
      return 15;
    case "nose":
      return 10;
    case "fuselage":
      return 5;
    case "engine":
      return 4;
    case "fin":
      return 3;
    case "booster":
      return 2;
    default:
      return 0;
  }
}

/** Whether a grid point falls inside a placed part's hit box (matches builder hit areas). */
export function placementContainsGridPoint(
  placed: PlacedPart,
  gridX: number,
  gridY: number,
): boolean {
  const part = PART_BY_ID[placed.partId];
  if (!part) return false;

  const dx = gridX - placed.x;
  const dy = gridY - placed.y;
  const hw = part.dimensions.gridW / 2;
  const hh = part.dimensions.gridH / 2;

  if (part.category === "fin") {
    return Math.abs(dx) <= hw && Math.abs(dy) <= hh;
  }

  const rot = placed.rotation ?? 0;
  const cos = Math.cos(-rot);
  const sin = Math.sin(-rot);
  const scaleX = placed.scaleX ?? 1;
  const localX = (dx * cos - dy * sin) / scaleX;
  const localY = dx * sin + dy * cos;

  return Math.abs(localX) <= hw && Math.abs(localY) <= hh;
}

/** Topmost same-category part under the cursor — drop here to swap the part in place. */
export function findReplacementTarget(
  part: PartDefinition,
  gridX: number,
  gridY: number,
  placements: PlacedPart[],
  excludeInstanceId?: string,
): PlacedPart | null {
  let best: PlacedPart | null = null;
  let bestZ = -Infinity;

  for (const placed of placements) {
    if (placed.instanceId === excludeInstanceId) continue;
    const existing = PART_BY_ID[placed.partId];
    if (!existing || existing.category !== part.category) continue;
    if (!placementContainsGridPoint(placed, gridX, gridY)) continue;

    const z = categoryZIndex(existing);
    if (z >= bestZ) {
      bestZ = z;
      best = placed;
    }
  }

  return best;
}
